<template>
  <div class="survey-wrap">

    <!-- ── 問卷列表 ── -->
    <div v-if="!currentSurvey" class="survey-list-page">
      <div class="page-header">
        <div>
          <h1 class="page-title">問卷調查</h1>
          <p class="page-sub">建立、發布問卷並查看回覆統計</p>
        </div>
        <button class="btn btn-primary" @click="openCreate">＋ 新增問卷</button>
      </div>

      <div v-if="loading" class="text-center py-12" style="color:var(--gray-400)">載入中…</div>

      <div v-else-if="surveys.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <div class="empty-title">尚無問卷</div>
        <div class="empty-desc">建立您的第一份問卷開始收集意見</div>
        <button class="btn btn-primary mt-4" @click="openCreate">＋ 新增問卷</button>
      </div>

      <div v-else class="survey-cards">
        <div v-for="s in surveys" :key="s.id" class="survey-card">
          <div class="sc-header">
            <div class="sc-title">{{ s.title }}</div>
            <span :class="['sc-badge', `badge-${s.status}`]">{{ statusLabel(s.status) }}</span>
          </div>
          <div class="sc-desc">{{ s.description || '（無說明）' }}</div>
          <div class="sc-meta">
            <span>📝 {{ s.response_count }} 份回覆</span>
            <span v-if="s.expires_at">📅 {{ fmtDate(s.expires_at) }} 截止</span>
            <span v-if="s.max_responses">上限 {{ s.max_responses }} 份</span>
            <span>{{ fmtDate(s.created_at) }}</span>
          </div>
          <div class="sc-actions">
            <button class="btn btn-sm btn-secondary" @click="openEditor(s)">✏️ 編輯</button>
            <button class="btn btn-sm btn-secondary" @click="openStats(s)">📊 統計</button>
            <button class="btn btn-sm btn-secondary" @click="copyLink(s)">🔗 連結</button>
            <button v-if="s.status === 'draft'" class="btn btn-sm btn-primary" @click="publish(s)">發布</button>
            <button v-if="s.status === 'published'" class="btn btn-sm btn-warning" @click="changeStatus(s,'closed')">關閉</button>
            <button v-if="s.status === 'closed'" class="btn btn-sm btn-secondary" @click="changeStatus(s,'published')">重新開放</button>
            <button class="btn btn-sm btn-danger" @click="deleteSurvey(s)">刪除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 問卷編輯器 ── -->
    <div v-else class="survey-editor">
      <div class="editor-topbar">
        <button class="btn btn-secondary btn-sm" @click="backToList">← 返回列表</button>
        <div class="editor-title-wrap">
          <span class="editor-survey-title">{{ currentSurvey.title }}</span>
          <span :class="['sc-badge', `badge-${currentSurvey.status}`]">{{ statusLabel(currentSurvey.status) }}</span>
        </div>
        <div class="flex gap-2">
          <button class="btn btn-sm btn-secondary" @click="openStats(currentSurvey)">📊 統計</button>
          <a :href="`/s/${currentSurvey.slug}`" target="_blank" class="btn btn-sm btn-secondary">🔗 預覽</a>
          <button v-if="currentSurvey.status === 'draft'" class="btn btn-sm btn-primary" @click="publish(currentSurvey)">發布問卷</button>
        </div>
      </div>

      <!-- 編輯器 tab -->
      <div class="editor-tabs">
        <button :class="['etab', editorTab === 'questions' && 'active']" @click="editorTab = 'questions'">📝 題目管理</button>
        <button :class="['etab', editorTab === 'settings' && 'active']" @click="editorTab = 'settings'; loadSettings()">⚙️ 問卷設定</button>
      </div>

      <!-- 題目管理 -->
      <div v-if="editorTab === 'questions'" class="editor-body">
        <!-- 左：題目列表 -->
        <div class="q-list">
          <div class="q-list-header">
            <span class="font-semibold">題目列表</span>
            <span style="color:var(--gray-400);font-size:.8rem;">{{ questions.length }} 題</span>
          </div>
          <div v-if="questions.length === 0" class="q-empty">尚未新增任何題目</div>
          <draggable v-else v-model="questions" item-key="id" handle=".drag-handle" @end="onReorder" class="q-items">
            <template #item="{ element: q, index: idx }">
              <div :class="['q-item', selectedQIdx === idx && 'q-item-active']" @click="selectQ(idx)">
                <span class="drag-handle">⣿</span>
                <div class="q-item-body">
                  <span class="q-num">Q{{ idx + 1 }}</span>
                  <span class="q-type-badge">{{ typeLabel(q.type) }}</span>
                  <div class="q-item-title">{{ q.title }}</div>
                </div>
                <button class="q-del" @click.stop="deleteQ(q.id)">✕</button>
              </div>
            </template>
          </draggable>
          <button class="btn-add-q" @click="openAddQ">＋ 新增題目</button>
        </div>

        <!-- 右：題目編輯 -->
        <div class="q-editor-panel">
          <div v-if="selectedQIdx === null" class="q-editor-empty">
            <div>點選左側題目進行編輯</div>
            <div style="margin-top:8px;font-size:.8rem;color:var(--gray-400)">或點「新增題目」加入第一題</div>
          </div>
          <div v-else class="q-form">
            <div class="form-group">
              <label class="form-label">題型</label>
              <select v-model="editQ.type" class="input" @change="onTypeChange">
                <option value="single">單選題</option>
                <option value="multiple">多選題</option>
                <option value="text_short">簡答（單行文字）</option>
                <option value="text_long">詳答（多行文字）</option>
                <option value="rating">評分（1–5 星）</option>
                <option value="dropdown">下拉選單</option>
                <option value="nps">NPS（0–10 淨推薦值）</option>
                <option value="scale">量表（數字刻度）</option>
                <option value="date">日期</option>
                <option value="matrix">矩陣題</option>
                <option value="ranking">排序題</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">題目內容 <span style="color:#dc2626">*</span></label>
              <textarea v-model="editQ.title" class="input" rows="2" placeholder="請輸入題目…"></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">補充說明 <span style="color:var(--gray-400);font-weight:400">（填答者可見，選填）</span></label>
              <input v-model="editQ.description" class="input" placeholder="例：請依實際使用經驗作答">
            </div>
            <div class="form-group">
              <label class="form-label" style="display:flex;align-items:center;gap:8px;cursor:pointer;">
                <input type="checkbox" v-model="editQ.required" style="width:15px;height:15px;accent-color:#6366f1">
                必填題
              </label>
            </div>
            <div v-if="['single','multiple','dropdown','ranking'].includes(editQ.type)" class="form-group">
              <label class="form-label">{{ editQ.type === 'ranking' ? '排序項目' : '選項' }}</label>
              <div class="options-list">
                <div v-for="(opt, i) in editQ.options" :key="i" class="opt-row">
                  <input v-model="editQ.options[i]" class="input" placeholder="選項文字…">
                  <button class="btn-icon-del" @click="editQ.options.splice(i,1)">✕</button>
                </div>
              </div>
              <button class="btn-add-opt" @click="editQ.options.push('')">＋ 新增選項</button>
            </div>

            <!-- NPS / 量表標籤 -->
            <div v-if="['nps','scale'].includes(editQ.type)" class="form-group">
              <label class="form-label">刻度標籤</label>
              <div style="display:flex;gap:8px;">
                <input v-model="editQ.config.min_label" class="input" placeholder="最低分標籤（如：不推薦）">
                <input v-model="editQ.config.max_label" class="input" placeholder="最高分標籤（如：非常推薦）">
              </div>
              <div v-if="editQ.type === 'scale'" style="display:flex;gap:8px;margin-top:8px;">
                <input v-model.number="editQ.config.min" type="number" class="input" placeholder="最小值（預設 1）" style="max-width:160px">
                <input v-model.number="editQ.config.max" type="number" class="input" placeholder="最大值（預設 5）" style="max-width:160px">
              </div>
            </div>

            <!-- 矩陣：列 / 欄 -->
            <div v-if="editQ.type === 'matrix'" class="form-group">
              <label class="form-label">矩陣的「列」（每行一個項目）</label>
              <textarea class="input" rows="3" placeholder="每行一個列項目，例如：服務態度"
                :value="(editQ.config.rows||[]).join('\n')"
                @input="e => editQ.config.rows = e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)"></textarea>
              <label class="form-label" style="margin-top:8px;">矩陣的「欄」（評比選項，每行一個）</label>
              <textarea class="input" rows="3" placeholder="每行一個欄選項，例如：非常滿意"
                :value="(editQ.config.cols||[]).join('\n')"
                @input="e => editQ.config.cols = e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)"></textarea>
            </div>

            <!-- 選項配額（單選/下拉） -->
            <div v-if="['single','dropdown'].includes(editQ.type) && editQ.options.length" class="form-group">
              <label class="form-label">選項配額 <span style="color:var(--gray-400);font-weight:400">（額滿後無法再選，留空 = 不限）</span></label>
              <div v-for="(opt, i) in editQ.options.filter(o=>o)" :key="i" style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
                <span style="flex:1;font-size:.85rem;color:var(--gray-600);">{{ opt }}</span>
                <input type="number" min="0" class="input" style="max-width:120px" placeholder="不限"
                  :value="(editQ.config.quota||{})[opt]"
                  @input="e => { editQ.config.quota = editQ.config.quota || {}; if(e.target.value) editQ.config.quota[opt] = parseInt(e.target.value); else delete editQ.config.quota[opt]; }">
              </div>
            </div>

            <!-- 多分頁 -->
            <div class="form-group">
              <label class="form-label">所屬分頁 <span style="color:var(--gray-400);font-weight:400">（0,1,2…；同頁題目一起顯示，多頁自動出現進度與上/下一步）</span></label>
              <input v-model.number="editQ.page" type="number" min="0" class="input" style="max-width:140px" placeholder="0">
            </div>

            <!-- 邏輯跳轉（單選/下拉/NPS/量表） -->
            <div v-if="['single','dropdown','nps','scale'].includes(editQ.type)" class="form-group">
              <label class="form-label">邏輯跳轉 <span style="color:var(--gray-400);font-weight:400">（當此題答案 = 某值，跳到指定分頁或結束）</span></label>
              <div v-for="(rule, i) in (editQ.config.logic||[])" :key="i" style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                <span style="font-size:.8rem;color:var(--gray-500);">答案=</span>
                <input v-model="rule.eq" class="input" style="flex:1" placeholder="選項值/分數">
                <span style="font-size:.8rem;color:var(--gray-500);">→</span>
                <input v-model="rule.goto" class="input" style="max-width:130px" placeholder="分頁編號 或 submit">
                <button class="btn-icon-del" @click="editQ.config.logic.splice(i,1)">✕</button>
              </div>
              <button class="btn-add-opt" @click="editQ.config.logic = editQ.config.logic||[]; editQ.config.logic.push({eq:'',goto:''})">＋ 新增規則</button>
            </div>

            <div class="q-form-actions">
              <button class="btn btn-primary" :disabled="savingQ" @click="saveQ">
                {{ savingQ ? '儲存中…' : '儲存題目' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 問卷設定 -->
      <div v-if="editorTab === 'settings'" class="settings-panel">
        <div class="settings-grid">
          <div class="settings-section">
            <div class="settings-section-title">感謝語</div>
            <div class="form-group">
              <label class="form-label">填答完成後顯示的訊息</label>
              <textarea v-model="settings.thank_you_message" class="input" rows="3"
                placeholder="例：感謝您撥冗填答，您的意見對我們非常重要！"></textarea>
            </div>
          </div>

          <div class="settings-section">
            <div class="settings-section-title">收集限制</div>
            <div class="form-group">
              <label class="form-label">截止日期時間 <span style="color:var(--gray-400);font-weight:400">（到期後自動關閉）</span></label>
              <input v-model="settings.expires_at" type="datetime-local" class="input">
              <button v-if="settings.expires_at" class="btn-clear-field" @click="settings.expires_at = ''">清除</button>
            </div>
            <div class="form-group">
              <label class="form-label">回覆上限 <span style="color:var(--gray-400);font-weight:400">（達到後自動關閉，0 = 不限）</span></label>
              <input v-model.number="settings.max_responses" type="number" min="0" class="input" style="max-width:140px" placeholder="0">
            </div>
          </div>

          <div class="settings-section">
            <div class="settings-section-title">防重複填答</div>
            <div class="form-group">
              <label class="form-label">防重複模式</label>
              <select v-model="settings.dedup_mode" class="input" style="max-width:240px">
                <option value="none">不限制（可重複填答）</option>
                <option value="ip">同一 IP 限一次</option>
                <option value="cookie">同一瀏覽器限一次（Cookie）</option>
                <option value="both">IP + Cookie 雙重</option>
              </select>
              <div style="font-size:.78rem;color:var(--gray-400);margin-top:4px">填有 Email 時，同 Email 也會擋重複。</div>
            </div>
          </div>

          <!-- 品牌主題 (Area C) -->
          <div class="settings-section">
            <div class="settings-section-title">🎨 品牌主題</div>
            <div class="form-group" style="display:flex;gap:16px;flex-wrap:wrap;">
              <div>
                <label class="form-label">主題色</label>
                <input type="color" v-model="settings.theme.primary" style="width:60px;height:38px;border:1px solid var(--border);border-radius:8px;cursor:pointer;">
              </div>
              <div>
                <label class="form-label">字型</label>
                <select v-model="settings.theme.font" class="input" style="max-width:200px">
                  <option value="system">系統預設</option>
                  <option value="noto-sans">思源黑體</option>
                  <option value="noto-serif">思源宋體</option>
                  <option value="rounded">圓體</option>
                </select>
              </div>
              <div>
                <label class="form-label">圓角</label>
                <select v-model="settings.theme.radius" class="input" style="max-width:140px">
                  <option value="none">直角</option><option value="sm">小</option>
                  <option value="md">中</option><option value="lg">大</option>
                </select>
              </div>
            </div>
          </div>

          <!-- 歡迎頁 / 結束轉址 (Area A) -->
          <div class="settings-section">
            <div class="settings-section-title">歡迎頁 / 結束</div>
            <div class="form-group">
              <label class="form-label">歡迎頁文字 <span style="color:var(--gray-400);font-weight:400">（填寫前先顯示，留空 = 直接開始）</span></label>
              <textarea v-model="settings.welcome_text" class="input" rows="3" placeholder="例：感謝您參與本次調查，預計花費 3 分鐘…"></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">完成後轉址 URL <span style="color:var(--gray-400);font-weight:400">（選填，填了就跳轉而非顯示感謝語）</span></label>
              <input v-model="settings.redirect_url" class="input" placeholder="https://...">
            </div>
          </div>

          <!-- 通知 / CRM (Area C) -->
          <div class="settings-section">
            <div class="settings-section-title">通知與整合</div>
            <div class="form-group">
              <label class="form-label">新回應通知信箱 <span style="color:var(--gray-400);font-weight:400">（選填）</span></label>
              <input v-model="settings.notify_email" type="email" class="input" placeholder="you@company.com">
            </div>
            <div class="form-group">
              <label class="form-label">Webhook URL <span style="color:var(--gray-400);font-weight:400">（選填，每筆回應 POST JSON）</span></label>
              <input v-model="settings.webhook_url" class="input" placeholder="https://hooks.example.com/...">
            </div>
            <div class="form-group">
              <label class="form-label" style="display:flex;align-items:center;gap:8px;cursor:pointer;">
                <input type="checkbox" v-model="settings.crm_create" style="width:15px;height:15px;accent-color:#6366f1">
                填答者 Email 自動建立 CRM 聯絡人
              </label>
            </div>
          </div>
        </div>

        <div class="settings-footer">
          <button class="btn btn-primary" :disabled="savingSettings" @click="saveSurveySettings">
            {{ savingSettings ? '儲存中…' : '儲存設定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 統計 Drawer ── -->
    <div v-if="statsDrawer.open" class="drawer-overlay" @click.self="statsDrawer.open = false">
      <div class="drawer">
        <div class="drawer-header">
          <span class="font-semibold">📊 {{ statsDrawer.survey?.title }}</span>
          <button @click="statsDrawer.open = false" class="btn-close-x">✕</button>
        </div>
        <div class="drawer-body">
          <div v-if="statsDrawer.loading" class="text-center py-8" style="color:var(--gray-400)">載入中…</div>
          <div v-else>
            <div class="stat-summary">共 <strong>{{ statsDrawer.data?.total_responses || 0 }}</strong> 份回覆</div>

            <!-- Trend chart -->
            <div v-if="statsDrawer.trend?.length" class="stat-card" style="padding:12px 16px;">
              <div class="stat-q-title" style="margin-bottom:8px;">📈 每日回覆趨勢（近 30 天）</div>
              <div style="height:140px;position:relative;">
                <canvas ref="trendChartRef"></canvas>
              </div>
            </div>

            <div v-for="q in (statsDrawer.data?.questions || [])" :key="q.question_id" class="stat-card">
              <div class="stat-q-title">{{ q.title }}</div>
              <div class="stat-answered">{{ q.answered }}/{{ q.total }} 人作答</div>

              <!-- Doughnut chart for choice questions -->
              <div v-if="q.distribution && q.answered > 0" style="height:150px;position:relative;margin:8px 0;">
                <canvas :id="`qchart-${q.question_id}`"></canvas>
              </div>
              <!-- Fallback text bars when no answers yet -->
              <div v-else-if="q.distribution" class="stat-bars">
                <div v-for="(cnt, opt) in q.distribution" :key="opt" class="stat-bar-row">
                  <div class="stat-bar-label">{{ opt }}</div>
                  <div class="stat-bar-track"><div class="stat-bar-fill" :style="{ width: barPct(cnt, q.answered) + '%' }"></div></div>
                  <div class="stat-bar-pct">{{ barPct(cnt, q.answered) }}%</div>
                  <div class="stat-bar-cnt">{{ cnt }}</div>
                </div>
              </div>

              <div v-if="(q.type === 'rating' || q.type === 'scale') && q.average != null" class="stat-avg">
                平均：<strong>{{ q.average }}</strong>{{ q.type === 'rating' ? ' / 5 ⭐' : '' }}
              </div>
              <!-- NPS -->
              <div v-if="q.type === 'nps'" class="stat-avg">
                <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
                  <div style="font-size:1.4rem;font-weight:800;" :style="{color: q.nps>=0 ? '#16a34a' : '#dc2626'}">NPS {{ q.nps ?? '—' }}</div>
                  <span style="font-size:.8rem;color:var(--gray-500);">推薦者 {{ q.promoters }} · 中立 {{ q.passives }} · 貶損者 {{ q.detractors }}</span>
                </div>
              </div>
              <!-- 矩陣 -->
              <div v-if="q.type === 'matrix' && q.matrix" style="overflow-x:auto;margin-top:8px;">
                <table class="mx-stat-table">
                  <tr><th></th><th v-for="c in q.cols" :key="c">{{ c }}</th></tr>
                  <tr v-for="(cells, row) in q.matrix" :key="row">
                    <th style="text-align:left;">{{ row }}</th>
                    <td v-for="c in q.cols" :key="c">{{ cells[c] || 0 }}</td>
                  </tr>
                </table>
              </div>
              <!-- 排序 -->
              <div v-if="q.type === 'ranking' && q.avg_rank" class="stat-bars" style="margin-top:8px;">
                <div v-for="(rank, opt) in sortedRank(q.avg_rank)" :key="opt" class="stat-bar-row">
                  <div class="stat-bar-label">{{ opt }}</div>
                  <div class="stat-bar-pct" style="width:auto;">平均名次 {{ rank ?? '—' }}</div>
                </div>
              </div>
              <div v-if="q.samples" class="stat-samples">
                <div v-for="(s, i) in q.samples" :key="i" class="stat-sample">{{ s }}</div>
              </div>
            </div>

            <!-- AI 洞察 -->
            <div class="stat-card" v-if="statsDrawer.data?.total_responses">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
                <span class="font-semibold" style="font-size:.9rem;">🤖 AI 洞察報告</span>
                <button class="btn btn-sm btn-ai" :disabled="insightsLoading" @click="loadInsights">
                  {{ insightsLoading ? '分析中…' : (insightsText ? '重新分析' : '產生洞察') }}
                </button>
              </div>
              <div v-if="insightsText" class="stat-samples" style="white-space:pre-wrap;margin-top:10px;line-height:1.7;">{{ insightsText }}</div>
            </div>

            <div class="mt-4" style="display:flex;gap:8px;">
              <a :href="`${ADMIN_API}/ai/survey/${statsDrawer.survey?.id}/export`"
                class="btn btn-secondary btn-sm" target="_blank">⬇ 匯出 CSV</a>
              <a :href="`${ADMIN_API}/ai/survey/${statsDrawer.survey?.id}/export.xls`"
                class="btn btn-secondary btn-sm" target="_blank">⬇ 匯出 Excel</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 新增問卷 Modal ── -->
    <div v-if="createModal.open" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,.5)">
      <div class="card" style="width:560px;max-width:95vw;">
        <div class="modal-header">
          <span class="font-semibold">新增問卷</span>
          <button @click="createModal.open = false" class="btn-close-x">✕</button>
        </div>

        <!-- 模式切換 -->
        <div class="px-5 pt-4">
          <div class="create-mode-tabs">
            <button :class="['cmt-btn', createModal.mode === 'manual' && 'active']" @click="createModal.mode = 'manual'">
              📝 手動建立
            </button>
            <button :class="['cmt-btn', createModal.mode === 'ai' && 'active']" @click="createModal.mode = 'ai'">
              ✨ AI 自動生成
            </button>
          </div>
        </div>

        <div class="p-5 space-y-4">
          <div>
            <label class="form-label">問卷標題 <span style="color:#dc2626">*</span></label>
            <input v-model="createModal.title" class="input" placeholder="例：2026 顧客滿意度調查" autocomplete="off">
          </div>
          <div>
            <label class="form-label">
              {{ createModal.mode === 'ai' ? '問卷目的與內容描述' : '說明' }}
              <span v-if="createModal.mode === 'ai'" style="color:#dc2626"> *</span>
              <span v-else style="color:var(--gray-400);font-weight:400">（選填）</span>
            </label>
            <textarea v-model="createModal.desc" class="input" rows="3"
              :placeholder="createModal.mode === 'ai'
                ? '例：我們想了解客戶對新產品功能的滿意度，包括易用性、速度、客服品質等面向，以利後續優化…'
                : '簡短說明問卷目的…'"></textarea>
          </div>
          <template v-if="createModal.mode === 'ai'">
            <div class="flex gap-3">
              <div style="flex:1">
                <label class="form-label">目標受眾 <span style="color:var(--gray-400);font-weight:400">（選填）</span></label>
                <input v-model="createModal.target_audience" class="input" placeholder="例：使用超過 3 個月的企業用戶">
              </div>
              <div style="flex:0 0 110px">
                <label class="form-label">題數</label>
                <select v-model.number="createModal.num_questions" class="input">
                  <option :value="5">5 題</option>
                  <option :value="8">8 題</option>
                  <option :value="10">10 題</option>
                  <option :value="12">12 題</option>
                </select>
              </div>
            </div>
            <div class="ai-gen-hint">
              💡 AI 將根據描述自動產生題目、選項與題型，生成後可在編輯器自由調整
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="createModal.open = false">取消</button>
          <button v-if="createModal.mode === 'manual'"
            class="btn btn-primary"
            :disabled="!createModal.title.trim() || createModal.loading"
            @click="doCreate">
            {{ createModal.loading ? '建立中…' : '建立問卷' }}
          </button>
          <button v-else
            class="btn btn-ai"
            :disabled="!createModal.title.trim() || !createModal.desc.trim() || createModal.loading"
            @click="doAICreate">
            {{ createModal.loading ? '✨ AI 生成中…' : '✨ AI 自動生成問卷' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 新增題目 Modal ── -->
    <div v-if="addQModal.open" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,.5)">
      <div class="card" style="width:520px;max-width:95vw;max-height:90vh;display:flex;flex-direction:column">
        <div class="modal-header">
          <span class="font-semibold">新增題目</span>
          <button @click="addQModal.open = false" class="btn-close-x">✕</button>
        </div>
        <div class="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label class="form-label">題型</label>
            <select v-model="addQModal.type" class="input" @change="addQModal.options = ['','']">
              <option value="single">單選題</option>
              <option value="multiple">多選題</option>
              <option value="text_short">簡答（單行文字）</option>
              <option value="text_long">詳答（多行文字）</option>
              <option value="rating">評分（1–5 星）</option>
              <option value="dropdown">下拉選單</option>
              <option value="nps">NPS（0–10）</option>
              <option value="scale">量表</option>
              <option value="date">日期</option>
              <option value="matrix">矩陣題</option>
              <option value="ranking">排序題</option>
            </select>
            <div style="font-size:.76rem;color:var(--gray-400);margin-top:4px">進階設定（刻度/矩陣/邏輯/分頁）可在新增後點題目編輯。</div>
          </div>
          <div>
            <label class="form-label">題目內容 <span style="color:#dc2626">*</span></label>
            <textarea v-model="addQModal.title" class="input" rows="2" placeholder="請輸入題目…"></textarea>
          </div>
          <div>
            <label class="form-label">補充說明 <span style="color:var(--gray-400);font-weight:400">（選填）</span></label>
            <input v-model="addQModal.description" class="input" placeholder="補充說明文字，填答者可見">
          </div>
          <div>
            <label class="form-label" style="display:flex;align-items:center;gap:8px;cursor:pointer;">
              <input type="checkbox" v-model="addQModal.required" style="width:15px;height:15px;accent-color:#6366f1">
              必填題
            </label>
          </div>
          <div v-if="['single','multiple','dropdown','ranking'].includes(addQModal.type)">
            <label class="form-label">{{ addQModal.type === 'ranking' ? '排序項目' : '選項' }}</label>
            <div class="options-list">
              <div v-for="(opt, i) in addQModal.options" :key="i" class="opt-row">
                <input v-model="addQModal.options[i]" class="input" :placeholder="`選項 ${i+1}`">
                <button class="btn-icon-del" @click="addQModal.options.splice(i,1)" :disabled="addQModal.options.length <= 1">✕</button>
              </div>
            </div>
            <button class="btn-add-opt" @click="addQModal.options.push('')">＋ 新增選項</button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="addQModal.open = false">取消</button>
          <button class="btn btn-primary" :disabled="!addQModal.title.trim() || addQModal.loading" @click="doAddQ">
            {{ addQModal.loading ? '新增中…' : '新增題目' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick } from 'vue'
import axios from 'axios'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { ADMIN_API } from '../store/app.js'

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` })

// ── State ──────────────────────────────────────────────────────────────────
const loading = ref(false)
const surveys = ref([])
const currentSurvey = ref(null)
const questions = ref([])
const selectedQIdx = ref(null)
const editQ = ref({ type: 'single', title: '', description: '', required: true, options: ['', ''], page: 0, config: {} })
const savingQ = ref(false)
const editorTab = ref('questions')
const savingSettings = ref(false)
const settings = reactive({
  thank_you_message: '',
  expires_at: '',
  max_responses: 0,
  allow_duplicate_ip: true,
  // C: 主題 + 通知/CRM；A: 歡迎/結束；D: 防重複模式
  theme_json: '',
  theme: { primary: '#6366f1', font: 'system', radius: 'md' },
  notify_email: '',
  webhook_url: '',
  crm_create: false,
  welcome_text: '',
  redirect_url: '',
  dedup_mode: 'ip',
})

const statsDrawer = reactive({ open: false, survey: null, data: null, trend: [], loading: false })
const insightsText = ref('')
const insightsLoading = ref(false)
const sortedRank = (avgRank) => Object.fromEntries(
  Object.entries(avgRank || {}).sort((a, b) => (a[1] ?? 99) - (b[1] ?? 99)))
const loadInsights = async () => {
  if (!statsDrawer.survey) return
  insightsLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/survey/${statsDrawer.survey.id}/insights`, { headers: authHeader() })
    insightsText.value = res.data?.insights || '（無內容）'
  } catch (e) {
    window.$toast?.error('AI 洞察失敗', e.response?.data?.detail || e.message)
  } finally {
    insightsLoading.value = false
  }
}
const trendChartRef = ref(null)
let _trendChart = null
const _qCharts = {}

function _destroyChart(chart) { try { chart?.destroy() } catch (_) {} }

watch(() => statsDrawer.open, async (open) => {
  if (!open) {
    _destroyChart(_trendChart); _trendChart = null
    Object.values(_qCharts).forEach(_destroyChart)
    Object.keys(_qCharts).forEach(k => delete _qCharts[k])
    return
  }
  await nextTick()
  _renderTrendChart()
  _renderQCharts()
})

watch(() => statsDrawer.data, async () => {
  if (!statsDrawer.open) return
  await nextTick()
  _renderTrendChart()
  _renderQCharts()
})

function _renderTrendChart() {
  const canvas = trendChartRef.value
  if (!canvas || !window.Chart) return
  _destroyChart(_trendChart)
  const labels = statsDrawer.trend.map(d => d.date)
  const data = statsDrawer.trend.map(d => d.count)
  _trendChart = new window.Chart(canvas, {
    type: 'bar',
    data: {
      labels,
      datasets: [{ label: '回覆數', data, backgroundColor: 'rgba(99,102,241,0.7)', borderRadius: 4 }]
    },
    options: {
      responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
    }
  })
}

function _renderQCharts() {
  if (!statsDrawer.data || !window.Chart) return
  statsDrawer.data.questions.forEach(q => {
    if (!q.distribution) return
    const canvas = document.getElementById(`qchart-${q.question_id}`)
    if (!canvas) return
    _destroyChart(_qCharts[q.question_id])
    const labels = Object.keys(q.distribution)
    const values = Object.values(q.distribution)
    _qCharts[q.question_id] = new window.Chart(canvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6'] }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'right', labels: { font: { size: 11 }, boxWidth: 14 } } }
      }
    })
  })
}
const createModal = reactive({
  open: false, mode: 'manual',
  title: '', desc: '', target_audience: '', num_questions: 8, loading: false
})
const addQModal = reactive({
  open: false, type: 'single', title: '', description: '',
  required: true, options: ['', ''], loading: false
})

// ── Load ──────────────────────────────────────────────────────────────────
onMounted(load)

async function load() {
  loading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/survey/`, { headers: authHeader() })
    surveys.value = res.data
  } catch (e) {
    window.$toast?.error('載入失敗', e.message)
  } finally {
    loading.value = false
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────
const statusLabel = s => ({ draft: '草稿', published: '發布中', closed: '已關閉' }[s] || s)
const typeLabel = t => ({ single: '單選', multiple: '多選', text_short: '簡答', text_long: '詳答', rating: '評分', dropdown: '下拉', nps: 'NPS', scale: '量表', date: '日期', matrix: '矩陣', ranking: '排序' }[t] || t)
const fmtDate = d => d ? new Date(d).toLocaleDateString('zh-TW') : ''
const barPct = (cnt, total) => total > 0 ? Math.round(cnt / total * 100) : 0

// ── Survey CRUD ───────────────────────────────────────────────────────────
const openCreate = () => {
  Object.assign(createModal, { open: true, mode: 'manual', title: '', desc: '', target_audience: '', num_questions: 8, loading: false })
}

const doCreate = async () => {
  if (!createModal.title.trim()) return
  createModal.loading = true
  try {
    const res = await axios.post(`${ADMIN_API}/ai/survey/`,
      { title: createModal.title.trim(), description: createModal.desc.trim() },
      { headers: authHeader() })
    createModal.open = false
    await load()
    openEditor(res.data)
  } catch (e) {
    window.$toast?.error('建立失敗', e.message)
  } finally {
    createModal.loading = false
  }
}

const doAICreate = async () => {
  if (!createModal.title.trim() || !createModal.desc.trim()) return
  createModal.loading = true
  try {
    const res = await axios.post(`${ADMIN_API}/ai/survey/ai-generate`, {
      title: createModal.title.trim(),
      description: createModal.desc.trim(),
      target_audience: createModal.target_audience.trim(),
      language: 'zh-TW',
      num_questions: createModal.num_questions
    }, { headers: authHeader() })
    createModal.open = false
    await load()
    const survey = res.data
    openEditorWithData(survey, survey.questions || [])
  } catch (e) {
    window.$toast?.error('AI 生成失敗', e.response?.data?.detail || e.message)
  } finally {
    createModal.loading = false
  }
}

const deleteSurvey = async (s) => {
  if (!confirm(`確定刪除「${s.title}」？所有回覆將一併刪除。`)) return
  await axios.delete(`${ADMIN_API}/ai/survey/${s.id}`, { headers: authHeader() })
  await load()
}

const publish = async (s) => changeStatus(s, 'published')

const changeStatus = async (s, status) => {
  await axios.put(`${ADMIN_API}/ai/survey/${s.id}`, { status }, { headers: authHeader() })
  if (currentSurvey.value?.id === s.id) currentSurvey.value.status = status
  await load()
}

const copyLink = (s) => {
  const url = `${location.origin}/s/${s.slug}`
  navigator.clipboard.writeText(url)
  window.$toast?.success('連結已複製', url)
}

// ── Editor ─────────────────────────────────────────────────────────────────
const openEditor = async (s) => {
  currentSurvey.value = s
  editorTab.value = 'questions'
  selectedQIdx.value = null
  await loadQuestions(s.id)
}

const openEditorWithData = (s, qs) => {
  currentSurvey.value = s
  editorTab.value = 'questions'
  selectedQIdx.value = null
  questions.value = qs.map(q => ({ ...q, options: q.options || [] }))
}

const backToList = () => {
  currentSurvey.value = null; questions.value = []; selectedQIdx.value = null
  load()
}

const loadQuestions = async (id) => {
  const res = await axios.get(`${ADMIN_API}/ai/survey/${id}/questions`, { headers: authHeader() })
  questions.value = res.data
}

const loadSettings = () => {
  if (!currentSurvey.value) return
  const s = currentSurvey.value
  settings.thank_you_message = s.thank_you_message || ''
  settings.expires_at = s.expires_at ? new Date(s.expires_at).toISOString().slice(0, 16) : ''
  settings.max_responses = s.max_responses || 0
  settings.allow_duplicate_ip = s.allow_duplicate_ip !== false && s.allow_duplicate_ip !== 0
  settings.notify_email = s.notify_email || ''
  settings.webhook_url = s.webhook_url || ''
  settings.crm_create = !!s.crm_create
  settings.welcome_text = s.welcome_text || ''
  settings.redirect_url = s.redirect_url || ''
  settings.dedup_mode = s.dedup_mode || (settings.allow_duplicate_ip ? 'none' : 'ip')
  try {
    settings.theme = s.theme_json ? { primary: '#6366f1', font: 'system', radius: 'md', ...JSON.parse(s.theme_json) }
                                  : { primary: '#6366f1', font: 'system', radius: 'md' }
  } catch { settings.theme = { primary: '#6366f1', font: 'system', radius: 'md' } }
}

const saveSurveySettings = async () => {
  savingSettings.value = true
  try {
    const payload = {
      thank_you_message: settings.thank_you_message || null,
      expires_at: settings.expires_at || '',
      max_responses: settings.max_responses > 0 ? settings.max_responses : 0,
      allow_duplicate_ip: settings.dedup_mode === 'none',
      dedup_mode: settings.dedup_mode,
      notify_email: settings.notify_email || '',
      webhook_url: settings.webhook_url || '',
      crm_create: settings.crm_create,
      welcome_text: settings.welcome_text || '',
      redirect_url: settings.redirect_url || '',
      theme_json: JSON.stringify(settings.theme)
    }
    const res = await axios.put(`${ADMIN_API}/ai/survey/${currentSurvey.value.id}`, payload, { headers: authHeader() })
    Object.assign(currentSurvey.value, res.data)
    window.$toast?.success('設定已儲存')
    await load()
  } catch (e) {
    window.$toast?.error('儲存失敗', e.message)
  } finally {
    savingSettings.value = false
  }
}

const selectQ = (idx) => {
  selectedQIdx.value = idx
  const q = questions.value[idx]
  editQ.value = {
    id: q.id, type: q.type, title: q.title,
    description: q.description || '',
    required: !!q.required,
    options: q.options?.length ? [...q.options] : ['', ''],
    page: q.page || 0,
    config: q.config ? JSON.parse(JSON.stringify(q.config)) : {}
  }
}

const onTypeChange = () => {
  if (!editQ.value.config) editQ.value.config = {}
  if (['single','multiple','dropdown','ranking'].includes(editQ.value.type)) {
    if (!editQ.value.options?.length) editQ.value.options = ['', '']
  }
}

const saveQ = async () => {
  if (!editQ.value.title?.trim()) return
  savingQ.value = true
  try {
    const payload = {
      type: editQ.value.type,
      title: editQ.value.title.trim(),
      description: editQ.value.description?.trim() || '',
      required: editQ.value.required,
      options: ['single','multiple','dropdown','ranking'].includes(editQ.value.type)
        ? editQ.value.options.filter(o => o.trim())
        : [],
      page: editQ.value.page || 0,
      config: editQ.value.config || {}
    }
    await axios.put(
      `${ADMIN_API}/ai/survey/${currentSurvey.value.id}/questions/${editQ.value.id}`,
      payload, { headers: authHeader() })
    await loadQuestions(currentSurvey.value.id)
    // re-sync editQ
    selectQ(selectedQIdx.value)
    window.$toast?.success('已儲存')
  } catch (e) {
    window.$toast?.error('儲存失敗', e.message)
  } finally {
    savingQ.value = false
  }
}

const deleteQ = async (qid) => {
  if (!confirm('確定刪除此題目？')) return
  await axios.delete(`${ADMIN_API}/ai/survey/${currentSurvey.value.id}/questions/${qid}`,
    { headers: authHeader() })
  selectedQIdx.value = null
  await loadQuestions(currentSurvey.value.id)
}

const onReorder = async () => {
  const ids = questions.value.map(q => q.id)
  await axios.post(`${ADMIN_API}/ai/survey/${currentSurvey.value.id}/questions/reorder`,
    { ids }, { headers: authHeader() })
}

// ── Add Q Modal ────────────────────────────────────────────────────────────
const openAddQ = () => {
  Object.assign(addQModal, { open: true, type: 'single', title: '', description: '', required: true, options: ['', ''], loading: false })
}

const doAddQ = async () => {
  if (!addQModal.title.trim()) return
  addQModal.loading = true
  try {
    const payload = {
      type: addQModal.type,
      title: addQModal.title.trim(),
      description: addQModal.description.trim(),
      required: addQModal.required,
      options: ['single','multiple','dropdown','ranking'].includes(addQModal.type)
        ? addQModal.options.filter(o => o.trim())
        : []
    }
    await axios.post(`${ADMIN_API}/ai/survey/${currentSurvey.value.id}/questions`,
      payload, { headers: authHeader() })
    addQModal.open = false
    await loadQuestions(currentSurvey.value.id)
    selectedQIdx.value = questions.value.length - 1
    selectQ(selectedQIdx.value)
  } catch (e) {
    window.$toast?.error('新增失敗', e.message)
  } finally {
    addQModal.loading = false
  }
}

// ── Stats ─────────────────────────────────────────────────────────────────
const openStats = async (s) => {
  insightsText.value = ''
  statsDrawer.survey = s; statsDrawer.open = true; statsDrawer.loading = true
  statsDrawer.data = null; statsDrawer.trend = []
  try {
    const [statsRes, trendRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/survey/${s.id}/stats`, { headers: authHeader() }),
      axios.get(`${ADMIN_API}/ai/survey/${s.id}/trend`, { headers: authHeader() }).catch(() => ({ data: [] })),
    ])
    statsDrawer.data = statsRes.data
    statsDrawer.trend = trendRes.data || []
  } catch (e) {
    window.$toast?.error('載入失敗', e.message)
  } finally {
    statsDrawer.loading = false
  }
}
</script>

<style scoped>
.survey-wrap { height: 100%; overflow: hidden; display: flex; flex-direction: column; }

/* ── List ── */
.survey-list-page { flex: 1; overflow-y: auto; padding: 28px 32px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; }
.page-title { font-size: 1.35rem; font-weight: 700; }
.page-sub { color: var(--gray-500); font-size: .875rem; margin-top: 2px; }
.empty-state { text-align: center; padding: 80px 20px; }
.empty-icon { font-size: 3rem; margin-bottom: 12px; }
.empty-title { font-size: 1.1rem; font-weight: 600; }
.empty-desc { color: var(--gray-500); font-size: .875rem; margin-top: 4px; }
.survey-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
.survey-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px; display: flex; flex-direction: column; gap: 8px; }
.sc-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.sc-title { font-weight: 600; font-size: .95rem; }
.sc-badge { padding: 2px 8px; border-radius: 12px; font-size: .72rem; font-weight: 600; white-space: nowrap; }
.badge-draft { background: #f1f5f9; color: #64748b; }
.badge-published { background: #dcfce7; color: #16a34a; }
.badge-closed { background: #fee2e2; color: #dc2626; }
.sc-desc { color: var(--gray-500); font-size: .8rem; }
.sc-meta { display: flex; flex-wrap: wrap; gap: 8px; font-size: .75rem; color: var(--gray-400); }
.sc-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }

/* ── Editor ── */
.survey-editor { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.editor-topbar { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid var(--border); background: var(--surface); flex-shrink: 0; }
.editor-title-wrap { flex: 1; display: flex; align-items: center; gap: 8px; font-size: .95rem; }
.editor-survey-title { font-weight: 600; }
.editor-tabs { display: flex; gap: 0; border-bottom: 1px solid var(--border); background: var(--surface); flex-shrink: 0; padding: 0 16px; }
.etab { padding: 10px 16px; font-size: .85rem; font-weight: 500; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; color: var(--gray-500); margin-bottom: -1px; transition: .15s; }
.etab.active { color: #6366f1; border-bottom-color: #6366f1; }
.etab:hover:not(.active) { color: var(--gray-700); }
.editor-body { flex: 1; display: flex; overflow: hidden; }

/* Q list */
.q-list { width: 280px; flex-shrink: 0; border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; background: var(--surface); }
.q-list-header { padding: 12px 14px; display: flex; justify-content: space-between; border-bottom: 1px solid var(--border); flex-shrink: 0; }
.q-empty { padding: 20px 14px; color: var(--gray-400); font-size: .8rem; text-align: center; }
.q-items { flex: 1; overflow-y: auto; }
.q-item { display: flex; align-items: center; gap: 8px; padding: 10px 12px; cursor: pointer; border-bottom: 1px solid var(--border); transition: .1s; }
.q-item:hover { background: var(--gray-50, #f8fafc); }
.q-item-active { background: #eff6ff !important; border-left: 3px solid #6366f1; }
.drag-handle { cursor: grab; color: var(--gray-300); font-size: .9rem; flex-shrink: 0; }
.q-item-body { flex: 1; min-width: 0; }
.q-num { font-size: .7rem; font-weight: 700; color: var(--gray-400); }
.q-type-badge { font-size: .65rem; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 1px 5px; margin-left: 4px; }
.q-item-title { font-size: .8rem; color: var(--gray-700); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.q-del { background: none; border: none; cursor: pointer; color: var(--gray-300); font-size: .8rem; padding: 2px 4px; }
.q-del:hover { color: #dc2626; }
.btn-add-q { margin: 10px 12px; padding: 8px; border: 1px dashed var(--border); background: none; border-radius: 6px; cursor: pointer; color: #6366f1; font-size: .8rem; width: calc(100% - 24px); flex-shrink: 0; }
.btn-add-q:hover { background: #eff6ff; }

/* Q editor panel */
.q-editor-panel { flex: 1; overflow-y: auto; padding: 24px; }
.q-editor-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: var(--gray-400); font-size: .9rem; }
.q-form { max-width: 560px; }
.form-group { margin-bottom: 16px; }
.q-form-actions { margin-top: 24px; }
.options-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px; }
.opt-row { display: flex; gap: 8px; align-items: center; }
.btn-icon-del { background: none; border: none; cursor: pointer; color: var(--gray-400); font-size: .8rem; padding: 4px; flex-shrink: 0; }
.btn-icon-del:hover { color: #dc2626; }
.btn-add-opt { background: none; border: 1px dashed var(--border); border-radius: 6px; padding: 6px 12px; font-size: .8rem; color: #6366f1; cursor: pointer; }
.btn-add-opt:hover { background: #eff6ff; }

/* Settings panel */
.settings-panel { flex: 1; overflow-y: auto; padding: 24px; display: flex; flex-direction: column; gap: 0; }
.settings-grid { display: flex; flex-direction: column; gap: 20px; max-width: 600px; }
.settings-section { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 20px; }
.settings-section-title { font-weight: 700; font-size: .9rem; margin-bottom: 14px; color: var(--gray-700); }
.settings-footer { max-width: 600px; margin-top: 20px; }
.btn-clear-field { margin-top: 6px; background: none; border: none; font-size: .78rem; color: #dc2626; cursor: pointer; padding: 0; }
.btn-clear-field:hover { text-decoration: underline; }

/* Toggle */
.lp-toggle { width: 40px; height: 22px; border-radius: 11px; background: #e2e8f0; position: relative; cursor: pointer; transition: .2s; flex-shrink: 0; }
.lp-toggle.active { background: #6366f1; }
.lp-toggle-thumb { width: 16px; height: 16px; border-radius: 50%; background: #fff; position: absolute; top: 3px; left: 3px; transition: .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.lp-toggle.active .lp-toggle-thumb { transform: translateX(18px); }

/* ── Stats Drawer ── */
.drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 60; display: flex; justify-content: flex-end; }
.drawer { width: 580px; max-width: 95vw; background: var(--surface); display: flex; flex-direction: column; height: 100%; box-shadow: -4px 0 24px rgba(0,0,0,.12); }
.drawer-header { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; }
.drawer-body { flex: 1; overflow-y: auto; padding: 20px; }
.btn-close-x { background: none; border: none; cursor: pointer; font-size: 1rem; color: var(--gray-400); }
.btn-close-x:hover { color: var(--gray-700); }
.stat-summary { font-size: .9rem; color: var(--gray-500); margin-bottom: 16px; }
.stat-card { background: #f8fafc; border: 1px solid var(--border); border-radius: 8px; padding: 16px; margin-bottom: 14px; }
.stat-q-title { font-weight: 600; font-size: .9rem; margin-bottom: 4px; }
.stat-answered { font-size: .75rem; color: var(--gray-400); margin-bottom: 12px; }
.stat-bars { display: flex; flex-direction: column; gap: 8px; }
.stat-bar-row { display: flex; align-items: center; gap: 8px; font-size: .8rem; }
.stat-bar-label { width: 90px; flex-shrink: 0; color: var(--gray-600); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stat-bar-track { flex: 1; background: #e2e8f0; border-radius: 4px; height: 14px; overflow: hidden; }
.stat-bar-fill { height: 100%; background: linear-gradient(90deg,#6366f1,#818cf8); border-radius: 4px; transition: width .4s; }
.stat-bar-pct { width: 34px; text-align: right; color: var(--gray-500); }
.stat-bar-cnt { width: 22px; text-align: right; color: var(--gray-400); }
.mx-stat-table { width: 100%; border-collapse: collapse; font-size: .8rem; }
.mx-stat-table th, .mx-stat-table td { padding: 6px 8px; text-align: center; border-bottom: 1px solid var(--border); color: var(--gray-600); }
.mx-stat-table th { font-weight: 600; color: var(--gray-700); }
.stat-avg { font-size: .85rem; margin-top: 10px; color: var(--gray-600); }
.stat-samples { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.stat-sample { background: #fff; border-radius: 6px; padding: 8px 10px; font-size: .8rem; color: var(--gray-600); border-left: 3px solid #e2e8f0; }

/* ── Create Modal ── */
.create-mode-tabs { display: flex; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; width: fit-content; margin-bottom: 4px; }
.cmt-btn { padding: 7px 18px; font-size: .85rem; font-weight: 500; background: transparent; border: none; cursor: pointer; color: var(--gray-500); transition: .15s; }
.cmt-btn.active { background: #6366f1; color: #fff; }
.cmt-btn:not(.active):hover { background: var(--gray-100); }
.ai-gen-hint { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 10px 14px; font-size: .8rem; color: #1d4ed8; line-height: 1.5; }

/* ── Modal common ── */
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--border); }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--border); }

/* ── Buttons ── */
.btn-sm { padding: 5px 12px; font-size: .8rem; }
.btn-warning { background: #fef3c7; color: #b45309; border: 1px solid #fde68a; border-radius: 6px; }
.btn-danger { background: #fee2e2; color: #dc2626; border: 1px solid #fca5a5; border-radius: 6px; }
.btn-ai { background: linear-gradient(135deg,#7c3aed,#4f46e5); color: #fff; border: none; border-radius: 6px; padding: 8px 18px; font-size: .875rem; cursor: pointer; transition: opacity .15s; }
.btn-ai:hover:not(:disabled) { opacity: .9; }
.btn-ai:disabled { opacity: .5; cursor: not-allowed; }
</style>
